import {
  Box,
  BoxProps,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import axios, { CancelTokenSource } from 'axios';
import { useCombobox } from 'downshift';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { UserLocationIcon, LocationIcon } from '../../config/icons';
import { GeocoderFeature, GeocoderResult } from '../../types/global-types';
import { geocoderAxiosInstance } from '../../utils/api';

const geocoderFeatureTransform = (
  feature: GeocoderFeature
): TransformedGeocoderFeature => {
  return {
    country: feature.properties.country || null,
    name:
      feature.properties.name ||
      feature.properties.city ||
      feature.properties.postcode ||
      '',
    postcode: feature.properties.postcode || null,
    city: feature.properties.city || null,
    state: feature.properties.state || null,
    position: feature.geometry.coordinates,
  };
};

const transformedGeocoderFeatureToMetaString = (
  feature: TransformedGeocoderFeature
) => {
  const parts = [
    feature.postcode,
    feature.city,
    feature.state,
    feature.country,
  ].filter((i) => i);

  return parts.join(', ');
};

export interface TransformedGeocoderFeature {
  country: string | null;
  name: string;
  city: string | null;
  state: string | null;
  position: [number, number];
  postcode: string | null;
}

interface PlaceAutocompleteProps {
  id: string;
  initialValue?: string;
  placeholder: string;
  onSelectItem: (item: TransformedGeocoderFeature) => void;
  onChangeQuery?: (text: string) => void;
}

type PlaceAutocompleteBoxProps = PlaceAutocompleteProps & BoxProps;

const PlaceAutocomplete: React.FC<PlaceAutocompleteBoxProps> = ({
  id,
  placeholder,
  onSelectItem,
  initialValue,
  onChangeQuery,
  ...props
}) => {
  const toast = useToast();
  // const data = search_data.searchData[searchType];
  const [isLoading, setIsLoading] = useState(false);
  const [autocompleteData, setAutocompleteData] = useState<
    TransformedGeocoderFeature[]
  >([]);
  const cancelTokenRef = useRef<CancelTokenSource | null>(null);
  const [query, setQuery] = useState(initialValue);
  const [userLocation, setUserlocation] = useState<null | {
    latitude: number;
    longitude: number;
  }>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const fetch = useCallback(
    async (query, userLocation) => {
      if (query === 'Ort auf der Karte' || userLocation !== null) {
        return;
      }

      if (query && query.length >= 3) {
        setIsLoading(true);

        if (cancelTokenRef.current) {
          cancelTokenRef.current.cancel();
        }

        const cancelToken = axios.CancelToken;
        cancelTokenRef.current = cancelToken.source();

        try {
          const result = await geocoderAxiosInstance.get<GeocoderResult>(
            '/api',
            {
              params: {
                q: query,
              },
              cancelToken: cancelTokenRef.current.token,
            }
          );

          if (result.data && result.status === 200) {
            const transformedData = result.data.features
              .filter((f) => f.properties.name)
              .map((feature) => geocoderFeatureTransform(feature));

            setAutocompleteData(transformedData);
            cancelTokenRef.current = null;
          } else {
            setAutocompleteData([]);
          }
        } catch (err) {}

        setIsLoading(false);
      }
    },
    [setIsLoading, setAutocompleteData]
  );

  useEffect(() => {
    if (timeoutRef?.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      fetch(query, userLocation);
    }, 500);
  }, [query, userLocation]);

  // useDebounce(
  //     async () => {
  //         if (query === 'Ort auf der Karte' || userLocation !== null) {
  //             return;
  //         }

  //         if (query && query.length >= 3) {
  //             setIsLoading(true);

  //             if (cancelTokenRef.current) {
  //                 cancelTokenRef.current.cancel();
  //             }

  //             const cancelToken = axios.CancelToken;
  //             cancelTokenRef.current = cancelToken.source();

  //             try {
  //                 const result = await geocoderAxiosInstance.get<GeocoderResult>(
  //                     '/api',
  //                     {
  //                         params: {
  //                             q: query,
  //                         },
  //                         cancelToken: cancelTokenRef.current.token,
  //                     }
  //                 );

  //                 if (result.data && result.status === 200) {
  //                     const transformedData = result.data.features
  //                         .filter((f) => f.properties.name)
  //                         .map((feature) =>
  //                             geocoderFeatureTransform(feature)
  //                         );

  //                     setAutocompleteData(transformedData);
  //                     cancelTokenRef.current = null;
  //                 } else {
  //                     setAutocompleteData([]);
  //                 }
  //             } catch (err) {}

  //             setIsLoading(false);
  //         }
  //     },
  //     500,
  //     [query, userLocation]
  // );

  useEffect(() => {
    if (initialValue) {
      setAutocompleteData([]);
      setInputValue(initialValue);
    }
  }, [initialValue]);

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    setInputValue,
    closeMenu,
  } = useCombobox<TransformedGeocoderFeature>({
    defaultInputValue: query || '',
    id,
    items: autocompleteData,
    onInputValueChange: (changes) => {
      if (changes.inputValue) {
        setUserlocation(null);
        setQuery(changes.inputValue);
        onChangeQuery && onChangeQuery(changes.inputValue);
      }
    },
    itemToString: (item) => {
      return item.name;
    },
    onSelectedItemChange: (state) => {
      if (state.selectedItem) {
        setUserlocation(null);
        onSelectItem(state.selectedItem);
        closeMenu();
      }
    },
  });

  const onGetGeolocation = () => {
    if (navigator.geolocation) {
      setIsLoading(true);

      navigator.geolocation.getCurrentPosition(
        async (e) => {
          // Geocode
          try {
            const result = await geocoderAxiosInstance.get<GeocoderResult>(
              '/reverse',
              {
                params: {
                  lat: e.coords.latitude,
                  lon: e.coords.longitude,
                },
              }
            );

            if (result.data && result.status === 200) {
              if (result.data.features && result.data.features.length > 0) {
                const newQuery = `Dein Standort: ${result.data.features[0].properties.city}`;

                setInputValue(newQuery);
                setQuery(newQuery);
                setUserlocation({
                  latitude: e.coords.latitude,
                  longitude: e.coords.longitude,
                });
                const transformedFeature = geocoderFeatureTransform(
                  result.data.features[0]
                );
                transformedFeature.position = [
                  e.coords.longitude,
                  e.coords.latitude,
                ];
                onSelectItem(transformedFeature);
                onChangeQuery && onChangeQuery(newQuery);
              }
            } else {
              toast({
                title: 'Fehler',
                description: 'Der Standort konnte nicht ermittelt werden.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
                position: 'top',
              });
            }
          } catch (err) {}

          setIsLoading(false);
        },
        () => {
          setIsLoading(false);
          toast({
            title: 'Fehler',
            description:
              'Der Standort konnte nicht ermittelt werden. Bitte schalte die Standort-Funktionalität im Browser frei.',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top',
          });
        }
      );
    } else {
      toast({
        title: 'Fehler',
        description:
          'Der Standort konnte nicht ermittelt werden. Dein Browser unterstützt diese Funktion nicht.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    }
  };

  return (
    <Box flex={1} pos="relative" {...props}>
      <Box {...getComboboxProps()}>
        <InputGroup flex={1}>
          <InputLeftElement
            zIndex={2}
            children={<LocationIcon color="gray.300" />}
          />
          <Input name="query" placeholder={placeholder} {...getInputProps()} />

          <InputRightElement
            marginRight="1px"
            children={
              <Flex alignItems="center">
                {isLoading ? (
                  <Spinner />
                ) : (
                  <Tooltip
                    zIndex={99}
                    hasArrow
                    label="Aktuellen Standort ermitteln"
                    placement="top"
                  >
                    <IconButton
                      borderLeftRadius="0"
                      h="calc(2.5rem - 2px)"
                      bg="gray.50"
                      borderLeftWidth="1px"
                      borderLeftColor="gray.300"
                      _hover={{
                        color: 'gray.900',
                        bg: 'gray.200',
                      }}
                      aria-label="Aktuellen Standort ermitteln"
                      icon={<UserLocationIcon color="gray.600" />}
                      onClick={onGetGeolocation}
                    />
                  </Tooltip>
                )}
              </Flex>
            }
          />
        </InputGroup>
      </Box>
      <Box
        as="ul"
        pos="absolute"
        listStyleType="none"
        left={0}
        right={0}
        top="100%"
        bg="#fff"
        display={!isOpen ? 'none' : 'block'}
        border="1px"
        borderColor="gray.200"
        maxH="300px"
        overflowY="auto"
        shadow="md"
        zIndex={9999}
        {...getMenuProps()}
      >
        {isOpen &&
          autocompleteData.map((item, index) => {
            return (
              <Box
                as="li"
                paddingY={2}
                paddingX={4}
                bg={highlightedIndex === index ? 'blue.100' : '#fff'}
                cursor="pointer"
                key={`${item}${index}`}
                {...getItemProps({ item, index })}
              >
                <Box>{item.name}</Box>
                <Box fontSize="sm" color="gray.500">
                  {transformedGeocoderFeatureToMetaString(item)}
                </Box>
              </Box>
            );
          })}
      </Box>
    </Box>
  );
};

export default PlaceAutocomplete;
